import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DollarSign, RotateCcw, AlertTriangle, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const card = "bg-white dark:bg-neutral-900 border border-brand-line dark:border-neutral-800 rounded-2xl";

export default function Payments() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const load = () => api.get("/admin/payments").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/payment-status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const refund = async (sid) => {
    const reason = window.prompt("Enter a refund reason (optional), then click OK to confirm the FULL refund:");
    if (reason === null) return;
    try { const { data: r } = await api.post("/admin/refund", { session_id: sid, reason }); toast.success(`Refunded $${r.refunded}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const badge = (s) => ({ paid: "bg-green-100 text-green-700", refunded: "bg-blue-100 text-blue-700", pending: "bg-amber-100 text-amber-700", failed: "bg-red-100 text-red-700", expired: "bg-neutral-200 text-neutral-600" }[s] || "bg-neutral-200");

  return (
    <div data-testid="payments-page">
      <h1 className="font-display text-4xl">Payments</h1>
      <p className="text-brand-ink/60 dark:text-white/50 mt-2">PayPal revenue, refunds and payout reports.</p>

      {status && (
        <div className={`${card} mt-6 p-5 flex items-center justify-between flex-wrap gap-3`} data-testid="paypal-status-card">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-brand-red" />
            <div>
              <div className="font-medium">PayPal · <span className={status.mode === "live" ? "text-green-600" : "text-amber-600"}>{status.mode === "live" ? "Live" : "Sandbox"} mode</span></div>
              <div className="text-xs text-brand-ink/40 dark:text-white/40 font-mono">Client ID {status.client_id_masked || "—"}</div>
            </div>
          </div>
          <span className={`inline-flex items-center gap-2 text-sm font-semibold px-3 py-1.5 rounded-full ${status.connected ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`} data-testid="paypal-connection-status">
            <span className={`w-2 h-2 rounded-full ${status.connected ? "bg-green-500" : "bg-red-500"}`} /> {status.detail}
          </span>
        </div>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-4">
        <Stat label="Total Revenue" value={`$${(data?.total_revenue || 0).toFixed(2)}`} icon={DollarSign} />
        <Stat label="Refunded" value={`$${(data?.refunded || 0).toFixed(2)}`} icon={RotateCcw} />
        <Stat label="Paid Transactions" value={data?.paid_count || 0} icon={DollarSign} />
        <Stat label="Failed / Expired" value={data?.failed_count || 0} icon={AlertTriangle} />
      </div>

      <div className={`${card} mt-6 overflow-hidden`}>
        <div className="px-6 py-4 border-b border-brand-line dark:border-neutral-800 font-display text-xl">Recent transactions</div>
        {!data || data.transactions.length === 0 ? <div className="p-6 text-brand-ink/50 dark:text-white/40" data-testid="payments-empty">No transactions yet. Take a test payment from the public site.</div> : (
          <div className="divide-y divide-brand-line dark:divide-neutral-800">
            {data.transactions.map((t) => (
              <div key={t.session_id} className="px-6 py-4 flex items-center justify-between flex-wrap gap-3" data-testid={`txn-${t.session_id.slice(0, 12)}`}>
                <div>
                  <div className="font-medium capitalize">{t.kind} <span className="text-brand-ink/40 dark:text-white/40 text-sm">· {(t.created_at || "").slice(0, 10)}</span></div>
                  <div className="text-xs text-brand-ink/40 dark:text-white/40 font-mono">{t.session_id.slice(0, 24)}…</div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-display text-xl">${(t.amount || 0).toFixed(2)}</span>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${badge(t.payment_status)}`}>{t.payment_status}</span>
                  {t.payment_status === "paid" && <button onClick={() => refund(t.session_id)} className="inline-flex items-center gap-1 text-sm text-brand-red hover:underline" data-testid={`refund-${t.session_id.slice(0, 12)}`}><RotateCcw className="w-4 h-4" /> Refund</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <p className="mt-4 text-xs text-brand-ink/40 dark:text-white/40">Note: refunds are processed through PayPal. Payout transfers are managed in your PayPal Dashboard.</p>
    </div>
  );
}

const cardCls = "bg-white dark:bg-neutral-900 border border-brand-line dark:border-neutral-800 rounded-2xl";
const Stat = ({ label, value, icon: Icon }) => (
  <div className={`${cardCls} p-5`} data-testid={`pay-stat-${label.toLowerCase().split(" ")[0]}`}>
    <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-[0.15em] text-brand-ink/50 dark:text-white/40">{label}</span><Icon className="w-5 h-5 text-brand-red" /></div>
    <div className="mt-3 font-display text-3xl">{value}</div>
  </div>
);
